/**
 * WebAudio engine. The sim owns time: the shell reports sim-time each frame
 * and the music scheduler keeps the AudioContext clock slaved to it with a
 * look-ahead scheduler (docs/ARCHITECTURE.md §7).
 *
 * Sync model: anchorAudioTime maps songTime 0 onto the context clock. Drift
 * between (ctx.currentTime - anchor) and reported sim time is corrected softly
 * beyond 25 ms and re-anchored beyond 250 ms.
 */
import { midiToFreq, type Track } from './track';

const LOOKAHEAD_S = 0.2;
const SOFT_DRIFT_S = 0.025;
const HARD_DRIFT_S = 0.25;

export interface AudioSettings {
  musicVolume: number; // 0..1
  sfxVolume: number; // 0..1
  /** User latency calibration, seconds (positive = audio scheduled later). */
  offsetS: number;
}

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private master!: GainNode;
  private musicGain!: GainNode;
  private sfxGain!: GainNode;
  private noiseBuf!: AudioBuffer;
  private settings: AudioSettings = { musicVolume: 0.8, sfxVolume: 0.8, offsetS: 0 };

  // Music scheduling state
  private track: Track | null = null;
  private anchorAudioTime = 0;
  private scheduledUntilSong = 0; // songTime seconds scheduled so far
  private playing = false;
  private liveNodes: Set<AudioScheduledSourceNode> = new Set();

  /** Must be called from a user gesture (browser autoplay policy). */
  unlock(): void {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') void this.ctx.resume();
      return;
    }
    this.ctx = new AudioContext();
    this.master = this.ctx.createGain();
    this.master.connect(this.ctx.destination);
    this.musicGain = this.ctx.createGain();
    this.musicGain.connect(this.master);
    this.sfxGain = this.ctx.createGain();
    this.sfxGain.connect(this.master);
    this.applySettings(this.settings);

    // 1s of white noise reused by hats/snares/death SFX.
    const len = this.ctx.sampleRate;
    this.noiseBuf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const data = this.noiseBuf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  }

  get isReady(): boolean {
    return this.ctx !== null;
  }

  applySettings(s: AudioSettings): void {
    this.settings = s;
    if (!this.ctx) return;
    this.musicGain.gain.value = s.musicVolume * 0.5;
    this.sfxGain.gain.value = s.sfxVolume;
  }

  // --- Music ----------------------------------------------------------------

  startMusic(track: Track, songOffsetS: number): void {
    if (!this.ctx) return;
    this.stopMusic();
    this.track = track;
    this.playing = true;
    this.anchorAudioTime = this.ctx.currentTime - songOffsetS + this.settings.offsetS;
    this.scheduledUntilSong = Math.max(0, songOffsetS);
  }

  stopMusic(): void {
    this.playing = false;
    this.track = null;
    for (const n of this.liveNodes) {
      try {
        n.stop();
      } catch {
        // Node may already have ended; stopping twice throws InvalidStateError.
      }
    }
    this.liveNodes.clear();
  }

  pauseMusic(): void {
    this.playing = false;
    for (const n of this.liveNodes) {
      try {
        n.stop();
      } catch {
        /* already ended */
      }
    }
    this.liveNodes.clear();
  }

  resumeMusic(simTimeS: number): void {
    if (!this.ctx || !this.track) return;
    this.playing = true;
    this.anchorAudioTime = this.ctx.currentTime - simTimeS + this.settings.offsetS;
    this.scheduledUntilSong = simTimeS;
  }

  /** Called every rendered frame with the current sim time (seconds since level start). */
  updateMusic(simTimeS: number): void {
    const ctx = this.ctx;
    const track = this.track;
    if (!ctx || !track || !this.playing) return;

    // Drift correction between audio clock and sim clock.
    const songNow = ctx.currentTime - this.anchorAudioTime;
    const drift = songNow - simTimeS;
    if (Math.abs(drift) > HARD_DRIFT_S) {
      this.anchorAudioTime = ctx.currentTime - simTimeS + this.settings.offsetS;
      this.scheduledUntilSong = simTimeS;
    } else if (Math.abs(drift) > SOFT_DRIFT_S) {
      this.anchorAudioTime += drift * 0.1;
    }

    const target = simTimeS + LOOKAHEAD_S;
    if (target <= this.scheduledUntilSong) return;
    this.scheduleWindow(this.scheduledUntilSong, target);
    this.scheduledUntilSong = target;
  }

  private songToAudioTime(songS: number): number {
    return this.anchorAudioTime + songS;
  }

  private scheduleWindow(fromS: number, toS: number): void {
    const track = this.track;
    if (!track) return;
    const spb = 60 / track.bpm; // seconds per beat
    const loopS = track.loopBeats * spb;

    const emit = (beatInLoop: number, loopStartS: number, fn: (t: number) => void): void => {
      const t = loopStartS + beatInLoop * spb;
      if (t >= fromS && t < toS) fn(this.songToAudioTime(t));
    };

    const firstLoop = Math.floor(fromS / loopS);
    const lastLoop = Math.floor(toS / loopS);
    for (let loop = firstLoop; loop <= lastLoop; loop++) {
      const loopStartS = loop * loopS;
      for (const b of track.kick) emit(b, loopStartS, (t) => this.kick(t));
      for (const b of track.hat) emit(b, loopStartS, (t) => this.hat(t));
      for (const b of track.snare) emit(b, loopStartS, (t) => this.snare(t));
      for (const n of track.bass)
        emit(n.beat, loopStartS, (t) => this.bassNote(t, midiToFreq(n.midi), n.len * spb, n.vel));
      for (const n of track.lead)
        emit(n.beat, loopStartS, (t) => this.leadNote(t, midiToFreq(n.midi), n.len * spb, n.vel));
      for (const n of track.pad)
        emit(n.beat, loopStartS, (t) => this.padNote(t, midiToFreq(n.midi), n.len * spb, n.vel));
    }
  }

  // --- Synth voices -----------------------------------------------------------

  private env(t: number, peak: number, attack: number, decay: number): GainNode {
    const ctx = this.ctx as AudioContext;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(peak, t + attack);
    g.gain.exponentialRampToValueAtTime(0.001, t + attack + decay);
    return g;
  }

  private tone(
    dest: GainNode,
    type: OscillatorType,
    freq: number,
    t: number,
    dur: number,
    peak: number,
    attack = 0.005,
  ): void {
    const ctx = this.ctx as AudioContext;
    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.value = freq;
    const g = this.env(t, peak, attack, dur);
    osc.connect(g);
    g.connect(dest);
    osc.start(t);
    osc.stop(t + attack + dur + 0.05);
    this.trackNode(osc);
  }

  private trackNode(n: AudioScheduledSourceNode): void {
    this.liveNodes.add(n);
    n.onended = () => this.liveNodes.delete(n);
  }

  private kick(t: number): void {
    const ctx = this.ctx as AudioContext;
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(150, t);
    osc.frequency.exponentialRampToValueAtTime(45, t + 0.1);
    const g = this.env(t, 1.0, 0.002, 0.13);
    osc.connect(g);
    g.connect(this.musicGain);
    osc.start(t);
    osc.stop(t + 0.2);
    this.trackNode(osc);
  }

  private noiseVoice(
    t: number,
    dur: number,
    peak: number,
    filterType: BiquadFilterType,
    freq: number,
  ): void {
    const ctx = this.ctx as AudioContext;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuf;
    const f = ctx.createBiquadFilter();
    f.type = filterType;
    f.frequency.value = freq;
    const g = this.env(t, peak, 0.001, dur);
    src.connect(f);
    f.connect(g);
    g.connect(this.musicGain);
    src.start(t);
    src.stop(t + dur + 0.05);
    this.trackNode(src);
  }

  private hat(t: number): void {
    this.noiseVoice(t, 0.04, 0.25, 'highpass', 8000);
  }

  private snare(t: number): void {
    this.noiseVoice(t, 0.12, 0.5, 'bandpass', 1800);
    this.tone(this.musicGain, 'triangle', 190, t, 0.08, 0.4);
  }

  private bassNote(t: number, freq: number, dur: number, vel: number): void {
    const ctx = this.ctx as AudioContext;
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.value = freq;
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.setValueAtTime(900, t);
    f.frequency.exponentialRampToValueAtTime(300, t + dur);
    const g = this.env(t, vel * 0.5, 0.004, dur);
    osc.connect(f);
    f.connect(g);
    g.connect(this.musicGain);
    osc.start(t);
    osc.stop(t + dur + 0.1);
    this.trackNode(osc);
  }

  private leadNote(t: number, freq: number, dur: number, vel: number): void {
    this.tone(this.musicGain, 'square', freq, t, dur, vel * 0.16);
    this.tone(this.musicGain, 'square', freq * 1.004, t, dur, vel * 0.1);
  }

  private padNote(t: number, freq: number, dur: number, vel: number): void {
    this.tone(this.musicGain, 'sawtooth', freq, t, dur, vel * 0.06, 0.5);
    this.tone(this.musicGain, 'sawtooth', freq * 0.996, t, dur, vel * 0.06, 0.5);
  }

  // --- SFX --------------------------------------------------------------------

  sfxDeath(): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    this.noiseSfx(t, 0.25, 0.8, 'lowpass', 1200);
    const osc = this.ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(400, t);
    osc.frequency.exponentialRampToValueAtTime(60, t + 0.3);
    const g = this.env(t, 0.5, 0.002, 0.3);
    osc.connect(g);
    g.connect(this.sfxGain);
    osc.start(t);
    osc.stop(t + 0.4);
  }

  sfxComplete(): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    [523.25, 659.25, 783.99, 1046.5].forEach((f, i) => {
      this.tone(this.sfxGain, 'triangle', f, t + i * 0.09, 0.35, 0.5);
    });
  }

  sfxClick(): void {
    if (!this.ctx) return;
    this.tone(this.sfxGain, 'triangle', 880, this.ctx.currentTime, 0.05, 0.3);
  }

  /** Latency calibration: schedule `beats` metronome clicks at `bpm` and
   * return their absolute AudioContext times ([] when audio is unavailable).
   * Every 4th click is accented so the player can find the downbeat. */
  startMetronome(bpm: number, beats: number): number[] {
    if (!this.ctx) return [];
    const spb = 60 / bpm;
    const t0 = this.ctx.currentTime + 0.6;
    const times: number[] = [];
    for (let i = 0; i < beats; i++) {
      const t = t0 + i * spb;
      this.tone(this.sfxGain, 'square', i % 4 === 0 ? 1320 : 880, t, 0.05, 0.5);
      times.push(t);
    }
    return times;
  }

  /** Current AudioContext clock, seconds (0 when audio is unavailable). */
  now(): number {
    return this.ctx?.currentTime ?? 0;
  }

  private noiseSfx(
    t: number,
    dur: number,
    peak: number,
    filterType: BiquadFilterType,
    freq: number,
  ): void {
    const ctx = this.ctx as AudioContext;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuf;
    const f = ctx.createBiquadFilter();
    f.type = filterType;
    f.frequency.value = freq;
    const g = this.env(t, peak, 0.001, dur);
    src.connect(f);
    f.connect(g);
    g.connect(this.sfxGain);
    src.start(t);
    src.stop(t + dur + 0.05);
  }
}
